import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import OrsaneMediaComponent from "./OrsaneMediaComponent";

export type OrsaneMediaNodeType = {
    url: string;
    fileName: string;
    contentType: string;
    id: string;
    href: string | undefined;
};

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        orsanemedia: {
            addOrsaneMedia: (props: OrsaneMediaNodeType) => ReturnType;
        };
    }
}

export const OrsaneMediaNode = Node.create({
    name: "orsanemedia",
    group: "block",
    inline: false,
    selectable: false,
    draggable: true,
    atom: true,

    addAttributes() {
        return {
            fileName: {
                default: null,
            },
            url: {
                default: null,
            },
            id: {
                default: null,
            },
            contentType: {
                default: "other",
            },
            href: {
                default: undefined,
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="orsanemedia"]',
            },
        ];
    },

    // used for serverside rendering for example
    renderHTML({ HTMLAttributes }) {
        const finalHref = HTMLAttributes.href || HTMLAttributes.url;
        const isImage = HTMLAttributes.contentType?.startsWith("image/");
        if (isImage) {
            return [
                "div",
                mergeAttributes({ "data-type": "orsanemedia", class: "orsanemedia--image" }, HTMLAttributes),
                [
                    "a",
                    {
                        href: finalHref,
                        target: "_blank",
                        rel: "noopener noreferrer",
                    },
                    [
                        "img",
                        {
                            src: HTMLAttributes.url,
                            alt: HTMLAttributes.fileName,
                        },
                        "",
                    ],
                ],
            ];
        }

        return [
            "div",
            mergeAttributes({ "data-type": "orsanemedia", class: "orsanemedia--other" }, HTMLAttributes),
            [
                "a",
                {
                    href: finalHref,
                    target: "_blank",
                    rel: "noopener noreferrer",
                },
                [
                    "svg",
                    {
                        xmlns: "http://www.w3.org/2000/svg",
                        width: "24",
                        height: "24",
                        fill: "none",
                        stroke: "currentColor",
                        "stroke-width": "2",
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        viewBox: "0 0 24 24",
                        class: "w-4 text-blue-900",
                    },
                    [
                        "path",
                        {
                            d: "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z",
                        },
                    ],
                    [
                        "path",
                        {
                            d: "M14 2v4a2 2 0 0 0 2 2h4",
                        },
                    ],
                ],
                HTMLAttributes.fileName,
                "",
            ],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(OrsaneMediaComponent);
    },

    addCommands() {
        return {
            addOrsaneMedia:
                (attributes: OrsaneMediaNodeType) =>
                ({ chain, state }) => {
                    // Get the current selection position
                    const { from } = state.selection;

                    // Insert the content and move cursor after it
                    const result = chain()
                        .insertContent({
                            type: this.name,
                            attrs: attributes,
                        })
                        // Move cursor to after the inserted node
                        // The node size is 1 for atom nodes
                        .setTextSelection(from + 1)
                        .run();

                    return result;
                },
        };
    },
});
